import { UserOutlined } from '@ant-design/icons';
import {
  Alert, Avatar, Button, Card, Checkbox, Descriptions, Empty, Input, Modal, Space, Spin, Timeline, Typography,
} from 'antd';
import rideKycApi from 'api/rideKycApi';
import AppConfigs from 'general/constants/AppConfigs';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DOCUMENT_LABELS, getRideKycErrorMessage, getRideKycStatusLabel, RideKycStatusTag, VEHICLE_TYPE_LABELS,
} from '../../rideKycConstants';

const { Text, Title } = Typography;

function RideKycDetailScreen() {
  const router = useRouter();
  const driverProfileId = router.query?.driverProfileId;
  const { user, permissionList } = useSelector((state) => state.auth);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reasonModal, setReasonModal] = useState(null);
  const [reason, setReason] = useState('');
  const [documentReasons, setDocumentReasons] = useState({});
  const [selfieUrl, setSelfieUrl] = useState(null);

  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canReview = isAdmin || permissionList?.some((permission) => permission?.code === 'RIDE_KYC_REVIEW');

  const loadDetail = useCallback(async () => {
    if (!driverProfileId) return;
    setDetailLoading(true);
    try {
      const response = await rideKycApi.getProfile(driverProfileId);
      setDetail(response?.profile || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRideKycErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [driverProfileId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Show the driver's own submitted "Ảnh chân dung" as the profile icon
  // while reviewing — the signed access URL expires quickly (max 15 min
  // server-side), so it's fetched fresh each time the profile loads rather
  // than reused across page visits.
  useEffect(() => {
    const selfieDocument = (detail?.documents || []).find((document) => document.type === 'SELFIE');
    if (!selfieDocument) {
      setSelfieUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await rideKycApi.createDocumentAccess(detail.driverProfileId, selfieDocument.documentId);
        const url = new URL(response?.access?.url, AppConfigs.env.baseUrl).toString();
        if (!cancelled) setSelfieUrl(url);
      } catch (error) {
        if (!cancelled) setSelfieUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [detail?.driverProfileId, detail?.documents]);

  const submitReview = async (action, reviewReason, documentReviews) => {
    if (!driverProfileId || reviewLoading) return;
    setReviewLoading(true);
    try {
      const response = await rideKycApi.reviewProfile(driverProfileId, {
        action,
        ...(reviewReason ? { reason: reviewReason.trim() } : {}),
        ...(documentReviews ? { documentReviews } : {}),
      });
      setDetail(response?.profile || null);
      setReasonModal(null);
      setReason('');
      setDocumentReasons({});
      ToastHelper.showSuccess(action === 'APPROVE' ? 'Đã duyệt hồ sơ' : 'Đã cập nhật kết quả duyệt');
    } catch (error) {
      ToastHelper.showError(getRideKycErrorMessage(error));
    } finally {
      setReviewLoading(false);
    }
  };

  const confirmApprove = () => Modal.confirm({
    title: 'Duyệt hồ sơ tài xế?',
    content: 'Phương tiện sẽ được bật cho tính năng xe ghép sau khi duyệt.',
    okText: 'Duyệt hồ sơ',
    cancelText: 'Hủy',
    onOk: () => submitReview('APPROVE'),
  });

  const openNeedsChanges = () => {
    setReasonModal('NEEDS_CHANGES');
    setReason('');
    setDocumentReasons({});
  };

  // Shortcut from a single document row: opens the same modal, pre-checked
  // to only that one document, so the admin doesn't have to scroll down and
  // re-find/re-tick it among every other document to request just this one.
  const openNeedsChangesForDocument = (documentId) => {
    setReasonModal('NEEDS_CHANGES');
    setReason('');
    setDocumentReasons({ [documentId]: '' });
  };

  const toggleDocumentIssue = (documentId, checked) => {
    setDocumentReasons((current) => {
      const next = { ...current };
      if (checked) next[documentId] = '';
      else delete next[documentId];
      return next;
    });
  };

  const submitNeedsChanges = () => {
    const documentReviews = (detail?.documents || []).map((document) => ({
      documentId: document.documentId,
      status: Object.prototype.hasOwnProperty.call(documentReasons, document.documentId)
        ? 'REJECTED'
        : 'APPROVED',
      ...(Object.prototype.hasOwnProperty.call(documentReasons, document.documentId)
        ? { reason: documentReasons[document.documentId].trim() }
        : {}),
    }));
    submitReview('NEEDS_CHANGES', null, documentReviews);
  };

  const selectedIssueIds = Object.keys(documentReasons);
  const canSubmitDocumentIssues = selectedIssueIds.length > 0
    && selectedIssueIds.every((documentId) => documentReasons[documentId].trim());

  const openDocument = async (document) => {
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    try {
      const response = await rideKycApi.createDocumentAccess(detail.driverProfileId, document.documentId);
      const url = new URL(response?.access?.url, AppConfigs.env.baseUrl).toString();
      if (popup) popup.location = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      if (popup) popup.close();
      ToastHelper.showError(getRideKycErrorMessage(error));
    }
  };

  const duplicateWarning = detail?.duplicateWarnings?.identityNumber || detail?.duplicateWarnings?.licenseNumber;
  const vehicle = (detail?.documents || []).find((document) => document.vehicle)?.vehicle;

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Duyệt KYC tài xế xe ghép',
            onPress: () => router.navigate(-1),
          },
          { title: `Hồ sơ #${driverProfileId}` },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={detailLoading}>
            {!detail && !detailLoading ? <Empty description="Không tải được hồ sơ" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <Avatar size={56} src={selfieUrl} icon={<UserOutlined />} />
                    <div>
                      <Title level={4} className="mb-0">
                        Hồ sơ #
                        {detail.driverProfileId}
                      </Title>
                      <Text type="secondary">{detail.user?.fullname || 'Chưa có tên'}</Text>
                    </div>
                  </div>
                  <RideKycStatusTag status={detail.status} />
                </div>

                {duplicateWarning && (
                  <Alert
                    type="error"
                    showIcon
                    message="Phát hiện dữ liệu có khả năng trùng"
                    description={`Trùng ${[
                      detail.duplicateWarnings.identityNumber ? 'CCCD' : null,
                      detail.duplicateWarnings.licenseNumber ? 'GPLX' : null,
                    ].filter(Boolean).join(' và ')} với hồ sơ khác. Cần đối chiếu tài liệu trước khi duyệt.`}
                  />
                )}

                <Descriptions title="Người đăng ký" bordered column={1} size="small">
                  <Descriptions.Item label="Họ tên">{detail.user?.fullname || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Liên hệ">{detail.user?.phone || detail.user?.email || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ngày sinh">{detail.dateOfBirth || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Địa chỉ thường trú">{detail.address || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Hạng GPLX">{detail.licenseClass || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Hạn GPLX">{detail.licenseExpiryDate || '—'}</Descriptions.Item>
                  <Descriptions.Item label="CCCD/GPLX đã nhập">
                    {detail.identityNumberProvided ? 'Có' : 'Thiếu'}
                    {' / '}
                    {detail.licenseNumberProvided ? 'Có' : 'Thiếu'}
                  </Descriptions.Item>
                  {detail.emergencyContact && (
                    <Descriptions.Item label="Liên hệ khẩn cấp">
                      {[
                        detail.emergencyContact.name,
                        detail.emergencyContact.phone,
                        detail.emergencyContact.relationship,
                      ].filter(Boolean).join(' · ') || '—'}
                    </Descriptions.Item>
                  )}
                  {detail.pendingReferrer && (
                    <Descriptions.Item label="Mã giới thiệu (chờ duyệt)">
                      {detail.pendingReferrer.fullname || 'Người dùng EVG'}
                      {' · '}
                      {detail.pendingReferrer.userCode}
                      {' — chỉ áp dụng khi hồ sơ này được duyệt'}
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {vehicle && (
                  <Descriptions title="Phương tiện" bordered column={1} size="small">
                    <Descriptions.Item label="Loại xe">
                      {VEHICLE_TYPE_LABELS[vehicle.vehicleType] || vehicle.vehicleType || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Biển số">{vehicle.plate || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Số ghế">{vehicle.seatCapacity ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Màu xe">{vehicle.color || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Năm sản xuất">{vehicle.manufactureYear || '—'}</Descriptions.Item>
                  </Descriptions>
                )}

                <div>
                  <Title level={5}>Tài liệu bảo vệ</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {(detail.documents || []).map((document) => (
                      <Card key={document.documentId} size="small">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                          <div>
                            <Text strong>{DOCUMENT_LABELS[document.type] || document.type}</Text>
                            <div>
                              <RideKycStatusTag status={document.status} />
                              {' '}
                              {document.expiryDate ? `Hết hạn: ${document.expiryDate}` : ''}
                            </div>
                            {document.reviewReason && <div><Text type="danger">{document.reviewReason}</Text></div>}
                            {document.vehicle && (
                              <Text type="secondary">
                                Xe:
                                {' '}
                                {document.vehicle.plate || `#${document.vehicle.vehicleId}`}
                              </Text>
                            )}
                          </div>
                          <Space>
                            <Button onClick={() => openDocument(document)}>Mở tài liệu</Button>
                            {canReview
                              && (detail.status === 'PENDING_REVIEW' || detail.status === 'APPROVED')
                              && document.status === 'APPROVED' && (
                                <Button onClick={() => openNeedsChangesForDocument(document.documentId)}>
                                  Yêu cầu bổ sung
                                </Button>
                            )}
                          </Space>
                        </div>
                      </Card>
                    ))}
                  </Space>
                </div>

                <div>
                  <Title level={5}>Lịch sử duyệt</Title>
                  {(detail.reviewHistory || []).length ? (
                    <Timeline
                      items={detail.reviewHistory.map((item) => ({
                        color: (() => {
                          if (item.statusAfter === 'APPROVED') return 'green';
                          if (item.statusAfter === 'REJECTED') return 'red';
                          return 'orange';
                        })(),
                        children: (
                          <div>
                            <Text strong>
                              {getRideKycStatusLabel(item.statusBefore)}
                              {' → '}
                              {getRideKycStatusLabel(item.statusAfter)}
                            </Text>
                            <div>
                              <Text type="secondary">
                                {item.actor?.fullname || 'Hệ thống'}
                                {' · '}
                                {item.createdAt ? new Date(item.createdAt).toLocaleString('vi-VN') : '—'}
                              </Text>
                            </div>
                            {item.reasonProvided && <Text type="secondary">Có ghi nhận lý do xử lý</Text>}
                          </div>
                        ),
                      }))}
                    />
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có lịch sử duyệt" />}
                </div>

                {detail.reviewReason && <Alert type="warning" showIcon message="Lý do xử lý gần nhất" description={detail.reviewReason} />}

                {(detail.status === 'PENDING_REVIEW' || detail.status === 'APPROVED') && (
                  canReview ? (
                    <Space wrap>
                      {detail.status === 'PENDING_REVIEW' && (
                        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={confirmApprove} loading={reviewLoading}>Duyệt</Button>
                      )}
                      <Button onClick={openNeedsChanges}>Yêu cầu sửa giấy tờ</Button>
                      {detail.status === 'PENDING_REVIEW' && (
                        <Button danger onClick={() => { setReasonModal('REJECT'); setReason(''); }}>Từ chối</Button>
                      )}
                    </Space>
                  ) : <Alert type="info" showIcon message="Tài khoản chỉ có quyền xem, không có RIDE_KYC_REVIEW." />
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title={reasonModal === 'REJECT' ? 'Từ chối hồ sơ' : 'Yêu cầu bổ sung hồ sơ'}
        open={Boolean(reasonModal)}
        okText="Xác nhận"
        cancelText="Hủy"
        confirmLoading={reviewLoading}
        okButtonProps={{
          danger: reasonModal === 'REJECT',
          disabled: reasonModal === 'NEEDS_CHANGES' ? !canSubmitDocumentIssues : !reason.trim(),
        }}
        onCancel={() => { setReasonModal(null); setReason(''); setDocumentReasons({}); }}
        onOk={() => (reasonModal === 'NEEDS_CHANGES'
          ? submitNeedsChanges()
          : submitReview(reasonModal, reason))}
      >
        {reasonModal === 'NEEDS_CHANGES' ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="Chỉ chọn giấy tờ cần thay đổi"
              description="Giấy tờ không được chọn sẽ được đánh dấu đã đạt và tài xế không phải tải lại."
            />
            {detail?.status === 'APPROVED' && (
              <Alert
                type="warning"
                showIcon
                message="Hồ sơ đã duyệt sẽ tạm chuyển về trạng thái chờ bổ sung"
                description="Chế độ tài xế của tài khoản này sẽ bị tắt tạm thời cho đến khi tài xế nộp lại giấy tờ được yêu cầu và hồ sơ được duyệt lại."
              />
            )}
            {(detail?.documents || []).map((document) => {
              const selected = Object.prototype.hasOwnProperty.call(documentReasons, document.documentId);
              return (
                <Card key={document.documentId} size="small" style={{ borderColor: selected ? '#ff4d4f' : undefined }}>
                  <Checkbox
                    checked={selected}
                    onChange={(event) => toggleDocumentIssue(document.documentId, event.target.checked)}
                  >
                    <Text strong>{DOCUMENT_LABELS[document.type] || document.type}</Text>
                  </Checkbox>
                  {selected && (
                    <Input.TextArea
                      style={{ marginTop: 12 }}
                      rows={2}
                      maxLength={2000}
                      showCount
                      value={documentReasons[document.documentId]}
                      placeholder="Nêu rõ lỗi của riêng giấy tờ này"
                      onChange={(event) => setDocumentReasons((current) => ({
                        ...current,
                        [document.documentId]: event.target.value,
                      }))}
                    />
                  )}
                </Card>
              );
            })}
          </Space>
        ) : (
          <Input.TextArea
            rows={5}
            maxLength={2000}
            showCount
            value={reason}
            placeholder="Nhập lý do cụ thể để tài xế biết cần xử lý gì"
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Modal>
    </div>
  );
}

export default RideKycDetailScreen;
